import React from "react";
import styled from "styled-components";
import { ExternalLink } from "components/ExternalLink";
import { track } from "utils/track";
import TokenSelector from "../../components/TokenSelector";
import EnvironmentSelector from "../../components/EnvironmentSelector";

import {
  CodeBlock,
  ShellCommand,
  CopyableCodeLine,
  InlineCode,
} from "onboarding/components/NumberedStep";
import {
  SWI_SNAP_HOST_AGENT,
  INSTALLER_URL_BASE,
  KB_URL_BASE,
} from "onboarding/constants";

const SCRIPT_URL = `${INSTALLER_URL_BASE}/solarwinds-snap-agent-installer.sh`;
const PACKAGECLOUD_ORG = "solarwinds";
const PACKAGECLOUD_REPO = "swisnap";
const APT_REPO_PATH = `/etc/apt/sources.list.d/${PACKAGECLOUD_REPO}.list`;
const YUM_REPO_NAME = `${PACKAGECLOUD_ORG}_${PACKAGECLOUD_REPO}`;
const YUM_REPO_PATH = `/etc/yum.repos.d/${YUM_REPO_NAME}.repo`;

const KNOWLEDGEBASE_URL = `${KB_URL_BASE}/host_infrastructure/host_agent/`;

const ManualLink = styled.span`
  color: ${p => p.theme.link};
  cursor: pointer;
  margin-top: 30px;
  text-decoration: underline;
`;

const PACKAGE_NAME = "solarwinds-snap-agent";

export const easyInstructions = (props, name) => {
  const {
    agentRecorderToken,
    showManualInstructions,
    environmentsWithTokenCount,
  } = props;

  const environmentSelect = {
    title: "Select Environment",
    content: <EnvironmentSelector />,
  };

  const tokenSelect = {
    title: "Select API Token",
    content: <TokenSelector />,
  };

  const steps = [
    {
      title: `Install the ${SWI_SNAP_HOST_AGENT} with our agent installer script:`,
      content: (
        <div>
          <div onClick={_ => track(`Copied ${name} easy installer script`)}>
            <CopyableCodeLine shell>
              sudo bash -c &quot;$(curl -sSL{" "}
              <ExternalLink href={SCRIPT_URL}>{SCRIPT_URL}</ExternalLink>
              )&quot; -s --token {agentRecorderToken}
            </CopyableCodeLine>
          </div>
          <p>
            The agent will report using the API token{" "}
            <InlineCode>{agentRecorderToken}</InlineCode>.
          </p>
          <p>
            Have questions? Check out the{" "}
            <ManualLink onClick={showManualInstructions}>
              manual install
            </ManualLink>{" "}
            or head to the{" "}
            <ExternalLink href={KNOWLEDGEBASE_URL}>documentation</ExternalLink>.
          </p>
        </div>
      ),
    },
  ];

  if (props.onboardingState.showModal) {
    return steps;
  } else if (environmentsWithTokenCount === 0) {
    steps.unshift(tokenSelect);
  } else {
    steps.unshift(environmentSelect, tokenSelect);
  }
  return steps;
};

const depRepoSetupInstructions = (os, dist) => (
  <div onClick={_ => track(`Copied ${os} deb repo setup instructions`)}>
    An apt sources file must be created with the following line.
    <br />
    We recommend saving it as <InlineCode>{APT_REPO_PATH}</InlineCode>.
    <br />
    Update the release string (e.g. &quot;<InlineCode>{dist}</InlineCode>&quot;)
    as needed for your respective systems.
    <CodeBlock>
      deb https://packagecloud.io/{PACKAGECLOUD_ORG}/{PACKAGECLOUD_REPO}/{os}/{" "}
      {dist} main
    </CodeBlock>
    Dependencies for GPG key verification and HTTPS repositories:
    <CodeBlock>
      <ShellCommand>sudo apt-get install debian-archive-keyring</ShellCommand>
      <ShellCommand>sudo apt-get install apt-transport-https</ShellCommand>
      <ShellCommand>
        curl -L -sS -o ./packagecloud.key https://packagecloud.io/{PACKAGECLOUD_ORG}/{PACKAGECLOUD_REPO}/gpgkey
      </ShellCommand>
      <ShellCommand>sudo apt-key add ./packagecloud.key</ShellCommand>
    </CodeBlock>
  </div>
);

const redhatRepoSetupInstructions = (os, dist) => (
  <div onClick={_ => track(`Copied ${os} repo setup instructions`)}>
    The following snippet should be written out to a yum repository
    configuration file.
    <br />
    We recommend saving it as <InlineCode>{YUM_REPO_PATH}</InlineCode>.
    <br />
    Update the release version (e.g. &quot;<InlineCode>
      {dist}
    </InlineCode>&quot;) as needed for your respective systems.{" "}
    {os === "amazonlinux" && (
      <span>
        Use <InlineCode>7</InlineCode> for Linux AMI version 2017.03 or{" "}
        <InlineCode>6</InlineCode> for versions 2016.03 and 2016.09.
      </span>
    )}
    <CodeBlock>
      [{YUM_REPO_NAME}]
      <br />
      name={YUM_REPO_NAME}
      <br />
      baseurl=https://packagecloud.io/{PACKAGECLOUD_ORG}/{PACKAGECLOUD_REPO}/el/{dist}/$basearch
      <br />
      repo_gpgcheck=1
      <br />
      gpgcheck=0
      <br />
      enabled=1
      <br />
      gpgkey=https://packagecloud.io/{PACKAGECLOUD_ORG}/{PACKAGECLOUD_REPO}/gpgkey
      <br />
      sslverify=1
      <br />
      sslcacert=/etc/pki/tls/certs/ca-bundle.crt
      <br />
      metadata_expire=300
    </CodeBlock>
    Dependencies for GPG key verification and HTTPS repositories:
    <CodeBlock>
      <ShellCommand>
        sudo yum install -y pygpgme --disablerepo=&apos;{YUM_REPO_NAME}&apos;
      </ShellCommand>
      <ShellCommand>
        sudo yum install -y yum-utils --disablerepo=&apos;{YUM_REPO_NAME}&apos;
      </ShellCommand>
    </CodeBlock>
    {os === "amazonlinux" && (
      <div>
        Enable the EPEL repository:
        <CodeBlock>
          <ShellCommand>yum install -y epel-release</ShellCommand>
          <ShellCommand>yum-config-manager --enable epel</ShellCommand>
        </CodeBlock>
      </div>
    )}
  </div>
);

const repositorySetupInstructions = (os, dist) => {
  switch (os) {
    case "ubuntu":
    case "debian":
      return depRepoSetupInstructions(os, dist);
    case "redhat":
    case "centos":
    case "amazonlinux":
      return redhatRepoSetupInstructions(os, dist);
    default:
      return null;
  }
};

const packageInstallInstructions = os => {
  switch (os) {
    case "ubuntu":
    case "debian":
      return (
        <div onClick={_ => track(`Copied ${os} Install the AO Agent Package`)}>
          <CodeBlock>
            <ShellCommand>sudo apt-get update</ShellCommand>
            <ShellCommand>sudo -E apt-get install {PACKAGE_NAME}</ShellCommand>
          </CodeBlock>
        </div>
      );
    case "redhat":
    case "centos":
    case "amazonlinux":
      return (
        <div onClick={_ => track(`Copied ${os} Install the AO Agent Package`)}>
          <CodeBlock>
            <ShellCommand>
              sudo yum -q makecache -y --disablerepo=&apos;*&apos;
              --enablerepo=&apos;{YUM_REPO_NAME}&apos;
            </ShellCommand>
            <ShellCommand>sudo -E yum install {PACKAGE_NAME}</ShellCommand>
          </CodeBlock>
        </div>
      );
    default:
      return null;
  }
};

export const manualInstructions = props => {
  const { agentRecorderToken, os, dist, environmentsWithTokenCount } = props;

  const environmentSelect = {
    title: "Select Environment",
    content: <EnvironmentSelector />,
  };

  const tokenSelect = {
    title: "Select API Token",
    content: <TokenSelector />,
  };

  let steps = [
    {
      title: "Set up package repository.",
      content: repositorySetupInstructions(os, dist),
    },

    {
      title: "Set SolarWinds API Token environment variable.",
      content: (
        <div onClick={_ => track(`Copied ${os} set SolarWinds token`)}>
          <CodeBlock>
            <ShellCommand>
              export SOLARWINDS_TOKEN=&quot;{agentRecorderToken}&quot;
            </ShellCommand>
          </CodeBlock>
        </div>
      ),
    },
    {
      title: `Install the ${SWI_SNAP_HOST_AGENT} package.`,
      content: packageInstallInstructions(os),
    },
  ];

  if (props.onboardingState.showModal) {
    return steps;
  } else if (environmentsWithTokenCount === 0) {
    steps.splice(1, 0, tokenSelect);
  } else {
    steps.splice(1, 0, environmentSelect, tokenSelect);
  }
  return steps;
};
